import React from 'react';
import { Calendar, Clock, Users, X, Sparkles } from 'lucide-react';

/**
 * SimilarRides — modal showing rides similar to one being created.
 *
 * Props:
 *   rides: array of {ride, score, reason} objects
 *   isOpen: boolean
 *   onClose: function
 *   onJoin: function(rideId) — callback to join a ride instead
 *   onProceed: function — callback to proceed with creation anyway
 */
function SimilarRides({ rides = [], isOpen, onClose, onJoin, onProceed }) {
  if (!isOpen || rides.length === 0) return null;

  const formatDate = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString + 'T00:00:00');
    return date.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' });
  };

  const formatTime = (timeString) => {
    if (!timeString) return '';
    const [hours, minutes] = timeString.split(':');
    const hour = parseInt(hours, 10);
    const ampm = hour >= 12 ? 'PM' : 'AM';
    const displayHour = hour % 12 || 12;
    return `${displayHour}:${minutes} ${ampm}`;
  };

  const formatTimeRange = (start, end) => {
    if (start && end) return `${formatTime(start)} - ${formatTime(end)}`;
    if (start) return formatTime(start);
    if (end) return formatTime(end);
    return 'Time TBD';
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content" onClick={(e) => e.stopPropagation()} style={{ maxWidth: '550px' }}>
        {/* Header */}
        <div style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'flex-start',
          marginBottom: '16px',
          paddingBottom: '16px',
          borderBottom: '1px solid #e5e7eb',
        }}>
          <div>
            <h3 style={{ margin: 0, fontSize: '1.15rem', fontWeight: '600', color: '#1F2937', display: 'flex', alignItems: 'center', gap: '8px' }}>
              <Sparkles size={18} style={{ color: '#f59e0b' }} />
              Similar Rides Found
            </h3>
            <p style={{ margin: '4px 0 0 0', fontSize: '0.8rem', color: '#6B7280' }}>
              These existing rides match your criteria. Join one instead?
            </p>
          </div>
          <button
            onClick={onClose}
            style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#6B7280', padding: '4px' }}
          >
            <X size={20} />
          </button>
        </div>

        {/* Ride list */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: '12px', maxHeight: '400px', overflowY: 'auto' }}>
          {rides.map((rec, index) => {
            const ride = rec.ride;
            const seatsLeft = ride.spotsAvailable - ride.spotsFilled;

            return (
              <div key={ride.requestId || index} style={{
                padding: '14px',
                backgroundColor: '#f8fafc',
                borderRadius: '8px',
                border: '1px solid #e5e7eb',
              }}>
                {/* Match reason */}
                <div style={{
                  fontSize: '0.75rem',
                  color: '#059669',
                  fontWeight: '500',
                  marginBottom: '8px',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '4px',
                }}>
                  <span style={{
                    backgroundColor: '#ecfdf5',
                    padding: '2px 8px',
                    borderRadius: '10px',
                    border: '1px solid #a7f3d0',
                  }}>
                    {Math.round(rec.score)}% match
                  </span>
                  <span style={{ color: '#6B7280', fontWeight: '400' }}>{rec.reason}</span>
                </div>

                {/* Ride info */}
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '12px', fontSize: '0.85rem', color: '#374151', marginBottom: '8px' }}>
                  <span style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                    <Calendar size={14} stroke="#00356B" /> {formatDate(ride.departureDate)}
                  </span>
                  <span style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                    <Clock size={14} stroke="#00356B" /> {formatTimeRange(ride.startTime, ride.endTime)}
                  </span>
                  <span style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                    <Users size={14} stroke="#00356B" /> {seatsLeft} {seatsLeft === 1 ? 'seat' : 'seats'} left
                  </span>
                </div>

                {/* Driver */}
                <div style={{ fontSize: '0.8rem', color: '#6B7280', marginBottom: '10px' }}>
                  Driver: {ride.owner || 'Unknown'}
                </div>

                {/* Join button */}
                <button
                  onClick={() => onJoin && onJoin(ride.requestId)}
                  style={{
                    padding: '6px 14px',
                    backgroundColor: '#00356B',
                    color: 'white',
                    border: 'none',
                    borderRadius: '6px',
                    cursor: 'pointer',
                    fontSize: '0.8rem',
                    fontWeight: '500',
                  }}
                >
                  Join This Ride
                </button>
              </div>
            );
          })}
        </div>

        {/* Footer */}
        <div style={{
          display: 'flex',
          justifyContent: 'space-between',
          marginTop: '20px',
          paddingTop: '16px',
          borderTop: '1px solid #e5e7eb',
        }}>
          <button
            onClick={onProceed}
            style={{
              padding: '8px 16px',
              backgroundColor: 'transparent',
              color: '#6B7280',
              border: '1px solid #d1d5db',
              borderRadius: '6px',
              cursor: 'pointer',
              fontSize: '0.85rem',
            }}
          >
            Create New Ride Anyway
          </button>
          <button
            onClick={onClose}
            style={{
              padding: '8px 16px',
              backgroundColor: '#f3f4f6',
              color: '#374151',
              border: 'none',
              borderRadius: '6px',
              cursor: 'pointer',
              fontSize: '0.85rem',
            }}
          >
            Cancel
          </button>
        </div>
      </div>
    </div>
  );
}

export default SimilarRides;
